import { Sphere } from '@react-three/drei';
import { colors } from '@ui';
import * as THREE from 'three';

import { getKeypointName } from '../helpers';

import type { KeypointIdToName } from '../types';

const HUGE_RADIUS = 0.035;
const LARGE_RADIUS = 0.025;
const MEDIUM_RADIUS = 0.018;
const SMALL_RADIUS = 0.01;
const HIDDEN = 0;

const radiusMap = {
    pelvis: HUGE_RADIUS,
    left_hip: LARGE_RADIUS,
    right_hip: LARGE_RADIUS,
    upper_torso: HUGE_RADIUS,
    head: LARGE_RADIUS,
    left_heel: MEDIUM_RADIUS,
    right_heel: MEDIUM_RADIUS,
    left_ankle: MEDIUM_RADIUS,
    right_ankle: MEDIUM_RADIUS,
    left_big_toe: SMALL_RADIUS,
    right_big_toe: SMALL_RADIUS,
    left_index_base: SMALL_RADIUS,
    right_index_base: SMALL_RADIUS,
    right_wrist: MEDIUM_RADIUS,
    left_wrist: MEDIUM_RADIUS,
    left_small_toe: HIDDEN,
    right_small_toe: HIDDEN,
    left_pinky_base: HIDDEN,
    right_pinky_base: HIDDEN,
    nose: HIDDEN,
    left_ear: HIDDEN,
    right_ear: HIDDEN,
    left_eye: HIDDEN,
    right_eye: HIDDEN,
};

function getJointColor(isComparison: boolean, isTranslucent: boolean, isHighlighted: boolean) {
    if (isComparison) return colors.slate[400];
    if (isTranslucent) return colors.slate[300];
    if (isHighlighted) return colors.slate[500];
    return colors.slate[500];
}

function getJointOpacity(isComparison: boolean, isTranslucent: boolean) {
    if (isComparison) return 0.5;
    if (isTranslucent) return 0.3;
    return 1;
}

export const Joint = ({
    position,
    pointId,
    keypointIdToName,
    isHighlighted = false,
    isTranslucent = false,
    isComparison = false,
}: {
    position: THREE.Vector3;
    pointId: number;
    keypointIdToName: KeypointIdToName;
    isHighlighted?: boolean;
    isTranslucent?: boolean;
    isComparison?: boolean;
}) => {
    const radius = radiusMap[getKeypointName(pointId, keypointIdToName) as keyof typeof radiusMap] ?? LARGE_RADIUS;

    if (radius === 0) {
        return null;
    }

    const color = getJointColor(isComparison, isTranslucent, isHighlighted);
    const opacity = getJointOpacity(isComparison, isTranslucent);

    return (
        <Sphere args={[radius, 16, 16]} position={position}>
            <meshStandardMaterial color={color} flatShading transparent opacity={opacity} />
        </Sphere>
    );
};
